/**
 * ContactHandleForm
 *
 * Shared form component for adding/editing carrier contact handles.
 * Used by both AddContactHandleButton and EditContactHandleModal.
 */

import React, { useCallback, useId } from 'react';

import type { HandleType } from '@/components/carrier-directory/types';
import {
  AddressAutocomplete,
  StateCodeSelect,
} from '@/components/ui/address-autocomplete';
import { Input } from '@/components/ui/input';

export interface ContactHandleFormState {
  handleType: HandleType;
  value: string;
  label: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}

export const INITIAL_FORM_STATE: ContactHandleFormState = {
  handleType: 'FAX',
  value: '',
  label: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  zip: '',
};

const HANDLE_TYPE_OPTIONS: { value: HandleType; label: string }[] = [
  { value: 'FAX', label: 'Fax' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'MAILING_ADDRESS', label: 'Mailing Address' },
  { value: 'PHONE', label: 'Phone' },
];

interface ContactHandleFormProps {
  state: ContactHandleFormState;
  onChange: (state: ContactHandleFormState) => void;
  showTypeSelector?: boolean;
}

const getPlaceholder = (handleType: HandleType): string => {
  switch (handleType) {
    case 'FAX':
    case 'PHONE':
      return '(555) 123-4567';
    case 'EMAIL':
      return 'claims@example.com';
    default:
      return '';
  }
};

const getValueLabel = (handleType: HandleType): string => {
  switch (handleType) {
    case 'FAX':
      return 'Fax Number';
    case 'EMAIL':
      return 'Email';
    case 'PHONE':
      return 'Phone';
    default:
      return 'Value';
  }
};

export const ContactHandleForm: React.FC<ContactHandleFormProps> = ({
  state,
  onChange,
  showTypeSelector = true,
}) => {
  const formId = useId();
  const isAddress = state.handleType === 'MAILING_ADDRESS';

  const updateField = useCallback(
    <K extends keyof ContactHandleFormState>(
      field: K,
      value: ContactHandleFormState[K],
    ) => {
      onChange({ ...state, [field]: value });
    },
    [state, onChange],
  );

  const handleTypeChange = useCallback(
    (newType: HandleType) => {
      onChange({
        ...state,
        handleType: newType,
        value: '',
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
      });
    },
    [state, onChange],
  );

  const handleAddressSelect = useCallback(
    (addr: {
      address_line1: string;
      address_line2: string | null;
      city: string;
      state: string;
      postal_code: string;
    }) => {
      onChange({
        ...state,
        addressLine1: addr.address_line1,
        addressLine2: addr.address_line2 || '',
        city: addr.city,
        state: addr.state,
        zip: addr.postal_code,
      });
    },
    [state, onChange],
  );

  return (
    <div className="space-y-4">
      {/* Handle type selector */}
      {showTypeSelector && (
        // eslint-disable-next-line jsx-a11y/label-has-associated-control -- select is nested inside label
        <label className="block space-y-1.5">
          <span className="text-[12px] font-medium text-zinc-600">Type</span>
          <select
            id={`${formId}-type`}
            value={state.handleType}
            onChange={(e) => handleTypeChange(e.target.value as HandleType)}
            className="w-full h-9 px-3 text-[13px] border border-zinc-200 rounded-md focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none bg-white"
          >
            {HANDLE_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      )}

      {/* Value field for non-address types */}
      {!isAddress && (
        <div className="space-y-1.5">
          <label
            htmlFor={`${formId}-value`}
            className="text-[12px] font-medium text-zinc-600"
          >
            {getValueLabel(state.handleType)}
          </label>
          <Input
            id={`${formId}-value`}
            value={state.value}
            onChange={(e) => updateField('value', e.target.value)}
            placeholder={getPlaceholder(state.handleType)}
            className="h-9 text-[13px]"
          />
        </div>
      )}

      {/* Address fields */}
      {isAddress && (
        <div className="space-y-3">
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- AddressAutocomplete renders an input with matching id */}
          <label className="block space-y-1.5">
            <span className="text-[12px] font-medium text-zinc-600">
              Address
            </span>
            <AddressAutocomplete
              id={`${formId}-address`}
              value={state.addressLine1}
              onChange={(val) => updateField('addressLine1', val)}
              onAddressSelect={handleAddressSelect}
              placeholder="Start typing address..."
              inputClassName="h-9 text-[13px]"
            />
          </label>
          <Input
            id={`${formId}-address2`}
            value={state.addressLine2}
            onChange={(e) => updateField('addressLine2', e.target.value)}
            placeholder="Suite, Unit, Dept..."
            className="h-9 text-[13px]"
            aria-label="Address line 2"
          />
          <div className="flex gap-2">
            <Input
              id={`${formId}-city`}
              value={state.city}
              onChange={(e) => updateField('city', e.target.value)}
              placeholder="City"
              className="flex-1 h-9 text-[13px]"
              aria-label="City"
            />
            <StateCodeSelect
              id={`${formId}-state`}
              value={state.state}
              onChange={(code) => updateField('state', code)}
              className="w-20 h-9 text-[13px]"
            />
            <Input
              id={`${formId}-zip`}
              value={state.zip}
              onChange={(e) => updateField('zip', e.target.value)}
              placeholder="ZIP"
              className="w-24 h-9 text-[13px]"
              aria-label="ZIP code"
            />
          </div>
        </div>
      )}

      {/* Label field */}
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- Input renders an input with matching id */}
      <label className="block space-y-1.5">
        <span className="text-[12px] font-medium text-zinc-600">
          Label <span className="text-zinc-400 font-normal">(optional)</span>
        </span>
        <Input
          id={`${formId}-label`}
          value={state.label}
          onChange={(e) => updateField('label', e.target.value)}
          placeholder="e.g., Lien Notices, Claims Dept..."
          className="h-9 text-[13px]"
        />
      </label>
    </div>
  );
};
